import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { ButtressEntity } from '@buttress/crag';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { callLambda } from '../../../../helpers/lambda.js';
import '@awesome.me/webawesome/dist/components/popover/popover.js';
import '../../../../ns-elements/ns-job-layout.js';
import definition from '@job-schemas/subscription/authorise-card-use.json';
import { jobSchema } from '../../job-schema.js';

interface PaymentMethod {
  id: string;
  ownerId: string;
  type: string;
  brand: string;
  last4: string;
  active: boolean;
}

@customElement(NSAuthoriseSubscriptionCardUse.is)
export default class NSAuthoriseSubscriptionCardUse extends NSJobComponent {
  static get is() {
    return 'ns-authorise-subscription-card-use';
  }

  private __subscription?: ButtressEntity;

  @state()
  private __caller?: {
    name: string;
    role: string;
    emails: string[];
    requestReason: string;
  };

  @state()
  private __selectedCard?: PaymentMethod;

  @state()
  private __company?: ButtressEntity;

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { reject: { type: 'hidden' }, accept: { type: 'hidden' } },
    });
  }

  static styles = [
    ...super.styles,
    css`
      .caller {
        padding: 0;
        border: 0;
        background: none;
        color: var(--wa-color-text-link);
        font: inherit;
        font-weight: 500;
        text-decoration: underline dotted;
        cursor: pointer;
      }

      wa-popover::part(body) {
        border: 0;
        border-radius: var(--wa-border-radius-m);
        background: var(--ns-color-inverse-surface);
        color: var(--ns-color-inverse-text);
        font-size: var(--wa-font-size-s);
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this._job) throw new Error('Unable to find job');

    if (!this._directory) throw new Error('Directory service not found');
    if (!this._directory.me || !this._directory.me.companyId)
      throw new Error('Company id not found in the directory service');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');

    this.__company = await this.loadJobCompany();

    this.__selectedCard = this.__subscription.paymentMethods.find(
      (p: PaymentMethod) => p.id === this._job?.inputValues.paymentMethodId,
    );

    this.__caller = JSON.parse(this._job?.inputValues.caller as string);
  }

  private async __accept() {
    this.clearError();
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory || !this._directory.me) throw new Error('Directory service not found');
      if (!this._job) throw new Error('Missing job');
      if (!this.__subscription) throw new Error('Can not find subscription');

      const subPaymentMethod = this.__subscription.paymentMethods.find(
        (p: PaymentMethod) => p.id === this._job?.inputValues.paymentMethodId,
      );
      if (!subPaymentMethod) throw new Error('Please select a valid payment method');

      // Already active means an earlier Accept set it and only its outcome failed, so just finish
      if (!subPaymentMethod.active) {
        this.loading('Selecting Subscription Card');
        const result = await callLambda(
          'subscription/select-card',
          'POST',
          this._auth.token,
          JSON.stringify({
            subscriptionId: this.__subscription.id,
            paymentMethodId: this._job.inputValues.paymentMethodId,
            personId: this._directory.me.id,
            jobId: this._job.id,
          }),
        );
        this.loaded();
        if ((result.res as unknown as { output: string }).output !== 'SUCCESSFULLY_SELECTED_CARD') {
          throw new Error("The card couldn't be set as the subscription's payment method");
        }
      }
      await this.outcome('accept');
    } catch (err: unknown) {
      this.loaded();
      this.showError(err, 'Something went wrong');
    }
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.outcome('reject')}>
        Reject
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__accept()}>Accept</wa-button>
    `;
  }

  render() {
    return html`
      <ns-job-layout>
        <div class="job-panel">
          <p>
            <button type="button" id="caller" class="caller">${this.__caller?.name}</button>
            requested to use the card that ends with ${this.__selectedCard?.last4} which belongs to you, to be the
            default payment method for the company's subscription.
          </p>
          <!-- Outside the <p>: its <div>s would close the paragraph -->
          <wa-popover for="caller" placement="bottom-start">
            <div class="layout vertical">
              <div class="mb-1">Role: ${this.__caller?.role.toUpperCase()}</div>
              <div class="mb-1">Email: ${this.__caller?.emails.join(', ')}</div>
              <div>Request Reason: ${this.__caller?.requestReason}</div>
            </div>
          </wa-popover>
        </div>
      </ns-job-layout>
    `;
  }
}
